import { useEffect, useRef, useState } from "react";

export default function CtaSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  /* ── Converging spiral canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    let t = 0;

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      const cx = W * 0.5;
      const cy = H * 0.42;

      /* outer glow */
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, W * 0.5);
      grd.addColorStop(0, "rgba(255,200,100,0.07)");
      grd.addColorStop(0.4, "rgba(255,180,80,0.03)");
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, W, H);

      /* converging DNA arms — 3 strands spiraling inward */
      for (let arm = 0; arm < 3; arm++) {
        const armPhase = (arm / 3) * Math.PI * 2;
        ctx.beginPath();
        let first = true;
        for (let i = 0; i <= 300; i++) {
          const frac = i / 300;
          /* spiral inward: starts from edge, converges to centre */
          const angle = frac * Math.PI * 8 + armPhase - t * 0.008;
          const r = (1 - frac) * Math.min(W, H) * 0.48;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          if (first) { ctx.moveTo(x, y); first = false; }
          else ctx.lineTo(x, y);
        }
        const alpha = 0.05 + Math.sin(t * 0.02 + arm * 1.2) * 0.025;
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* expanding rings */
      for (let ring = 0; ring < 5; ring++) {
        const phase = ((t * 0.005 + ring * 0.2) % 1);
        const r = phase * Math.min(W, H) * 0.5;
        const alpha = (1 - phase) * 0.09;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      /* concentric static rings */
      for (let ring = 1; ring <= 5; ring++) {
        const r = (ring / 5) * Math.min(W, H) * 0.46;
        const pulse = Math.sin(t * 0.012 + ring * 0.6) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,220,140,${0.025 + pulse * 0.03})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      /* centre convergence glow */
      const cGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, 40);
      cGrd.addColorStop(0, "rgba(255,220,140,0.18)");
      cGrd.addColorStop(1, "rgba(255,200,100,0)");
      ctx.beginPath();
      ctx.arc(cx, cy, 40, 0, Math.PI * 2);
      ctx.fillStyle = cGrd;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,220,140,0.8)";
      ctx.fill();

      /* floating dust */
      for (let p = 0; p < 40; p++) {
        const px = cx + Math.sin(t * 0.006 + p * 1.7) * W * 0.45;
        const py = cy + Math.cos(t * 0.005 + p * 1.1) * H * 0.4;
        const alpha = (Math.sin(t * 0.018 + p) * 0.5 + 0.5) * 0.1;
        ctx.beginPath();
        ctx.arc(px, py, 1, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,220,140,${alpha})`;
        ctx.fill();
      }

      t++;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); window.removeEventListener("resize", resize); };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => { if (entry.isIntersecting) setVisible(true); }); },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) { setError("—"); return; }
    setError("");
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("email", email);
      if (name) body.append("name", name);
      await fetch("https://readdy.ai/api/form/d7b8a249quqsda8uevfg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
      setFormOpen(false);
    } catch {
      setError("—");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="cta"
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh", background: "#000000" }}
    >
      {/* canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-0" />

      {/* bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none" style={{ height: "200px", background: "linear-gradient(to bottom, transparent, #000)" }} />

      <div
        className="relative z-20 flex flex-col items-center justify-center text-center px-6 md:px-8 pt-24 md:pt-52 pb-20 md:pb-32"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(32px)",
          transition: "opacity 2.2s ease, transform 2.2s ease",
        }}
      >
        {/* top ornament */}
        <div className="flex flex-col items-center gap-2 mb-12 md:mb-16">
          <div style={{ width: "1px", height: "50px", background: "linear-gradient(to bottom, transparent, rgba(255,220,140,0.35))" }} />
          <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "rgba(255,220,140,0.5)", boxShadow: "0 0 12px 4px rgba(255,200,100,0.25)" }} />
          <div style={{ width: "1px", height: "50px", background: "linear-gradient(to bottom, rgba(255,220,140,0.35), transparent)" }} />
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-4">
            <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "rgba(255,220,140,0.6)", boxShadow: "0 0 16px 5px rgba(255,200,100,0.3)" }} />
            <p
              className="text-white/35 tracking-[0.5em] uppercase text-xs"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              received
            </p>
            <p className="text-white/15 text-xs tracking-widest mt-2" style={{ fontFamily: "'Courier New', monospace" }}>
              一致した
            </p>
          </div>
        ) : formOpen ? (
          <form
            id="maverick-entry-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col items-center gap-3 w-full max-w-xs px-2"
          >
            <input
              type="text"
              name="name"
              placeholder="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-transparent border-b text-white/60 text-sm tracking-widest text-center outline-none placeholder-white/20"
              style={{
                fontFamily: "'Courier New', monospace",
                borderColor: "rgba(255,220,140,0.15)",
                padding: "18px 8px",
                minHeight: "56px",
              }}
            />
            <input
              type="email"
              name="email"
              required
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b text-white/60 text-sm tracking-widest text-center outline-none placeholder-white/20"
              style={{
                fontFamily: "'Courier New', monospace",
                borderColor: "rgba(255,220,140,0.15)",
                padding: "18px 8px",
                minHeight: "56px",
              }}
            />
            {error && (
              <p className="text-white/20 text-xs" style={{ fontFamily: "'Courier New', monospace" }}>{error}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="whitespace-nowrap text-white/45 text-xs tracking-[0.35em] uppercase cursor-pointer mt-4 rounded-full w-full"
              style={{
                fontFamily: "'Courier New', monospace",
                border: "1px solid rgba(255,220,140,0.18)",
                padding: "20px 40px",
                minHeight: "64px",
                transition: "color 0.5s ease, border-color 0.5s ease, box-shadow 0.5s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.color = "rgba(255,220,140,0.7)";
                el.style.borderColor = "rgba(255,220,140,0.35)";
                el.style.boxShadow = "0 0 20px rgba(255,200,100,0.1)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.color = "rgba(255,255,255,0.45)";
                el.style.borderColor = "rgba(255,220,140,0.18)";
                el.style.boxShadow = "none";
              }}
            >
              {submitting ? "…" : "SELECT"}
            </button>
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="whitespace-nowrap text-white/25 text-sm tracking-widest cursor-pointer mt-1"
              style={{
                fontFamily: "'Courier New', monospace",
                padding: "12px 24px",
                minHeight: "48px",
              }}
            >
              ←
            </button>
          </form>
        ) : (
          <div className="flex flex-col items-center gap-6">
            <p
              className="text-white/30 tracking-[0.3em] text-xs mb-2"
              style={{ fontFamily: "'Courier New', monospace" }}
            >
              — select yourself —
            </p>
            <button
              onClick={() => setFormOpen(true)}
              className="whitespace-nowrap text-white/40 text-sm tracking-[0.45em] uppercase cursor-pointer rounded-full"
              style={{
                fontFamily: "'Courier New', monospace",
                border: "1px solid rgba(255,220,140,0.18)",
                padding: "24px 64px",
                minHeight: "72px",
                minWidth: "200px",
                transition: "color 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.color = "rgba(255,220,140,0.65)";
                el.style.borderColor = "rgba(255,220,140,0.35)";
                el.style.boxShadow = "0 0 30px rgba(255,200,100,0.12), inset 0 0 20px rgba(255,200,100,0.04)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.color = "rgba(255,255,255,0.4)";
                el.style.borderColor = "rgba(255,220,140,0.18)";
                el.style.boxShadow = "none";
              }}
            >
              SELECT
            </button>
          </div>
        )}

        <div
          className="mt-24 md:mt-36 flex flex-col items-center gap-1"
          style={{ opacity: visible ? 1 : 0, transition: "opacity 3s ease 1.5s" }}
        >
          <div style={{ width: "1px", height: "50px", background: "linear-gradient(to bottom, rgba(255,220,140,0.15), transparent)", marginBottom: "16px" }} />
          <a
            href="https://store.maverick.pink/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-light cursor-pointer"
            style={{
              fontSize: "clamp(14px, 1.3vw, 16px)",
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
              fontWeight: 300,
              letterSpacing: "0.12em",
              lineHeight: 2.6,
              color: "rgba(255,255,255,0.35)",
              textDecoration: "none",
              transition: "color 0.5s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.color = "rgba(255,220,140,0.55)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.35)";
            }}
          >
            一致した者にだけ、現れる
          </a>
        </div>
      </div>

      {/* footer */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-6 md:px-14 py-6 md:py-8">
        <span className="text-white/10 text-xs tracking-[0.3em]" style={{ fontFamily: "'Courier New', monospace" }}>
          MAVERICK vol.5
        </span>
        <p className="text-xs" style={{ fontFamily: "'Courier New', monospace", color: "rgba(255,255,255,0.06)" }}>
          © 2026
        </p>
      </div>
    </section>
  );
}
